import { useEffect, useRef, useState, useCallback, type ReactNode } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  /** Max width class, e.g. "max-w-4xl" or "max-w-[1000px]" */
  maxWidth?: string;
  /** Max height class, e.g. "max-h-[90vh]" or "h-[85vh]" */
  maxHeight?: string;
  /** Show the default header with title + close button */
  showHeader?: boolean;
  /** Show a sticky footer close button */
  showFooterClose?: boolean;
  /** Custom z-index class */
  zIndex?: string;
  /** aria-label for the dialog (used if no title) */
  ariaLabel?: string;
  /** Additional class on the content panel */
  panelClassName?: string;
}

const ANIMATION_DURATION = 300;

export default function Modal({
  isOpen,
  onClose,
  children,
  title,
  maxWidth = 'max-w-4xl',
  maxHeight = 'max-h-[90vh]',
  showHeader = true,
  showFooterClose = false,
  zIndex = 'z-50',
  ariaLabel,
  panelClassName = '',
}: ModalProps) {
  const [visible, setVisible] = useState(false);
  const [animatingIn, setAnimatingIn] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const closingRef = useRef(false);
  const scrollYRef = useRef(0);
  const scrollLockedRef = useRef(false);

  // Lock body scroll - robust method that works on iOS too
  const lockScroll = useCallback(() => {
    if (scrollLockedRef.current) return;
    scrollYRef.current = window.scrollY;
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYRef.current}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    scrollLockedRef.current = true;
  }, []);

  const unlockScroll = useCallback(() => {
    if (!scrollLockedRef.current) return;
    const scrollY = scrollYRef.current;
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    scrollLockedRef.current = false;
    window.scrollTo(0, scrollY);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.documentElement.style.scrollBehavior = '';
      });
    });
  }, []);

  // Open: mount then animate in
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      lockScroll();
      closingRef.current = false;
      // Trigger animation after paint
      requestAnimationFrame(() => {
        setVisible(true);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimatingIn(true);
          });
        });
      });
    }
    // Safety cleanup if component unmounts while open
    return () => {
      if (isOpen) unlockScroll();
    };
  }, [isOpen, lockScroll, unlockScroll]);

  // Close with animation
  const handleClose = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setAnimatingIn(false);
    setTimeout(() => {
      unlockScroll();
      setVisible(false);
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
      onClose();
      closingRef.current = false;
    }, ANIMATION_DURATION);
  }, [onClose, unlockScroll]);

  // ESC key
  useEffect(() => {
    if (!visible) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [visible, handleClose]);

  // Focus trap
  useEffect(() => {
    if (!visible || !modalRef.current) return;
    const modal = modalRef.current;

    const getFocusable = () =>
      modal.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

    // Auto-focus first focusable element
    const timer = setTimeout(() => {
      const els = getFocusable();
      els[0]?.focus();
    }, 50);

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    modal.addEventListener('keydown', handleTab);
    return () => {
      clearTimeout(timer);
      modal.removeEventListener('keydown', handleTab);
    };
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      className={`fixed inset-0 ${zIndex} flex items-center justify-center p-4 transition-opacity duration-300 ease-out ${
        animatingIn ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={modalRef}
        className={`relative bg-white dark:bg-warm-gray-900 rounded-2xl shadow-2xl w-full ${maxWidth} ${maxHeight} overflow-hidden flex flex-col transition-all duration-300 ease-out will-change-transform ${
          animatingIn
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-95 translate-y-2'
        } ${panelClassName}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {showHeader && title && (
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-warm-gray-700 flex-shrink-0">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-warm-gray-100 pr-8">
              {title}
            </h2>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-warm-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-aurore-dark focus:ring-offset-2"
              aria-label="Fermer"
            >
              <X className="w-6 h-6 text-gray-600 dark:text-warm-gray-400" />
            </button>
          </div>
        )}

        {/* If no header but we still want a close button */}
        {!showHeader && (
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-lg bg-aurore-dark hover:bg-aurore-medium flex items-center justify-center transition-colors duration-200 shadow-md"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        )}

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {children}
        </div>

        {/* Footer close */}
        {showFooterClose && (
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-warm-gray-700 p-6 bg-white dark:bg-warm-gray-900">
            <button
              onClick={handleClose}
              className="w-full bg-aurore-dark text-white py-3 px-6 rounded-lg hover:bg-aurore-medium transition-colors font-semibold"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
